<template>
  <div class="page-wrap stack-lg account-history-page">
    <BaseCard class="account-history-card">
      <SectionTitle
        eyebrow="Счёт"
        title="История операций"
        subtitle="По вкладкам можно переключаться между бонусными изменениями и денежными транзакциями."
      />

      <div class="account-history-tabs">
        <button
          type="button"
          class="account-history-tab"
          :class="{ 'account-history-tab--active': activeTab === 'bonus' }"
          @click="setTab('bonus')"
        >
          История бонусного счёта
        </button>

        <button
          type="button"
          class="account-history-tab"
          :class="{ 'account-history-tab--active': activeTab === 'money' }"
          @click="setTab('money')"
        >
          Денежные транзакции
        </button>
      </div>

      <div class="account-history-summary">
        <div class="account-history-summary__item">
          <span>Текущий бонусный баланс</span>
          <strong>{{ bonusBalanceText }}</strong>
        </div>

        <div class="account-history-summary__item">
          <span>Всего денежных операций</span>
          <strong>{{ moneyItems.length }}</strong>
        </div>
      </div>

      <p v-if="error" class="message message--error">{{ error }}</p>
      <div v-else-if="loading" class="account-history-state">Загрузка истории…</div>

      <template v-else-if="activeTab === 'bonus'">
        <div v-if="bonusItems.length" class="account-history-list">
          <article
            v-for="item in bonusItems"
            :key="`bonus-${item.id}`"
            class="history-row"
          >
            <div class="history-row__head">
              <div class="history-row__amount" :class="amountClass(item.direction)">
                {{ formatBonusAmount(item) }}
              </div>
              <div class="history-row__date">{{ formatDateTime(item.created_at) }}</div>
            </div>

            <strong class="history-row__title">{{ bonusTitle(item) }}</strong>
            <p v-if="item.reason || item.comment" class="history-row__text">
              {{ item.reason || item.comment }}
            </p>

            <div class="history-row__meta">
              <span v-if="item.balance_after !== null && item.balance_after !== undefined">
                Баланс после: {{ formatBonusBalance(item.balance_after) }}
              </span>
              <span v-if="item.order_id">Заказ #{{ item.order_id }}</span>
              <span v-if="item.club_id">Клуб #{{ item.club_id }}</span>
            </div>
          </article>
        </div>

        <div v-else class="account-history-state">
          История бонусного счёта пока пустая.
        </div>
      </template>

      <template v-else>
        <div v-if="moneyItems.length" class="account-history-list">
          <article
            v-for="item in moneyItems"
            :key="`money-${item.id}`"
            class="history-row"
          >
            <div class="history-row__head">
              <div class="history-row__amount" :class="amountClass(item.direction)">
                {{ formatMoneyAmount(item) }}
              </div>
              <div class="history-row__date">{{ formatDateTime(item.created_at) }}</div>
            </div>

            <strong class="history-row__title">{{ moneyTitle(item) }}</strong>
            <p v-if="item.reason || item.comment || item.seat_label || item.tariff_name" class="history-row__text">
              {{ item.reason || item.comment || moneyDescription(item) }}
            </p>

            <div class="history-row__meta">
              <span>{{ moneyStatusLabel(item) }}</span>
              <span v-if="item.payment_method">{{ paymentMethodLabel(item.payment_method) }}</span>
              <span v-if="item.order_number">Заказ {{ item.order_number }}</span>
            </div>
          </article>
        </div>

        <div v-else class="account-history-state">
          Денежных транзакций пока нет.
        </div>
      </template>
    </BaseCard>
  </div>
</template>

<script>
import BaseCard from '../components/ui/BaseCard.vue';
import SectionTitle from '../components/ui/SectionTitle.vue';
import { extractMessage, isSuccess, post } from '../services/api';
import { formatDateTime, formatPrice } from '../utils/formatters';

export default {
  name: 'AccountHistoryView',
  components: {
    BaseCard,
    SectionTitle,
  },
  data() {
    return {
      loading: false,
      error: '',
      bonusItems: [],
      moneyItems: [],
      summary: {
        bonus_balance: 0,
      },
    };
  },
  computed: {
    user() {
      return this.$store.getters['auth/user'];
    },
    activeTab() {
      return this.$route.query.tab === 'money' ? 'money' : 'bonus';
    },
    bonusBalanceText() {
      const value = this.summary?.bonus_balance ?? this.user?.bonus_balance ?? 0;
      return `${Number(value || 0).toLocaleString('ru-RU')} BH`;
    },
  },
  watch: {
    'user.hash': {
      immediate: true,
      handler(hash) {
        if (!hash) return;
        this.loadHistory();
      },
    },
  },
  methods: {
    formatDateTime,
    setTab(tab) {
      const nextTab = tab === 'money' ? 'money' : 'bonus';
      if (nextTab === this.activeTab) return;
      this.$router.replace({
        path: '/account-history',
        query: nextTab === 'money' ? { tab: 'money' } : {},
      });
    },
    async loadHistory() {
      this.loading = true;
      this.error = '';

      try {
        const result = await post('/user/account-history', {
          hash: this.user?.hash,
        });

        if (!isSuccess(result)) {
          this.error = extractMessage(result, 'Не удалось загрузить историю счёта');
          return;
        }

        this.bonusItems = Array.isArray(result?.data?.bonus_history) ? result.data.bonus_history : [];
        this.moneyItems = Array.isArray(result?.data?.money_history) ? result.data.money_history : [];
        this.summary = result?.data?.summary || { bonus_balance: this.user?.bonus_balance || 0 };
      } catch (error) {
        this.error = 'Ошибка сети';
      } finally {
        this.loading = false;
      }
    },
    amountClass(direction) {
      return direction === 'out' ? 'history-row__amount--minus' : 'history-row__amount--plus';
    },
    formatBonusAmount(item) {
      const sign = item.direction === 'out' ? '−' : '+';
      return `${sign}${Number(item.amount || 0).toLocaleString('ru-RU')} BH`;
    },
    formatBonusBalance(value) {
      return `${Number(value || 0).toLocaleString('ru-RU')} BH`;
    },
    formatMoneyAmount(item) {
      const sign = item.direction === 'out' ? '−' : '+';
      return `${sign}${formatPrice(item.amount || 0)}`;
    },
    bonusTitle(item) {
      const map = {
        accrual: 'Начисление бонусов',
        writeoff: 'Списание бонусов',
        correction: 'Корректировка бонусов',
        adjustment: 'Изменение бонусного счёта',
      };

      const key = String(item.operation_type || '').toLowerCase();
      return map[key] || 'Изменение бонусного счёта';
    },
    moneyTitle(item) {
      const map = {
        payment: 'Оплата',
        partial: 'Частичная оплата',
        refund: 'Возврат средств',
        deposit: 'Пополнение счёта',
        withdrawal: 'Списание средств',
      };

      const key = String(item.transaction_type || '').toLowerCase();
      return map[key] || 'Денежная операция';
    },
    moneyDescription(item) {
      return [item.seat_label, item.tariff_name].filter(Boolean).join(' · ');
    },
    paymentMethodLabel(value) {
      const map = {
        card: 'Карта',
        cash: 'Наличные',
        online: 'Онлайн',
        sbp: 'СБП',
        bonus: 'Бонусы',
      };

      return map[String(value || '').toLowerCase()] || value;
    },
    moneyStatusLabel(item) {
      const map = {
        paid: 'Оплачено',
        partial: 'Частично оплачено',
        refunded: 'Возврат',
        completed: 'Проведено',
        pending: 'В обработке',
        cancelled: 'Отменено',
      };

      return map[String(item.status || '').toLowerCase()] || 'Операция';
    },
  },
};
</script>

<style scoped src="./AccountHistoryView.css"></style>